(function () {
  'use strict';

  if (customElements.get('pdp-product-v2')) return;

  class PdpProductV2 extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.slides = Array.from(this.querySelectorAll('[data-gallery-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.quantityOptions = Array.from(this.querySelectorAll('[data-quantity-option]'));
      this.purchaseOptions = Array.from(this.querySelectorAll('[data-purchase-option]'));
      this.form = this.querySelector('[data-pdp-v2-form]');
      this.variantSelector = this.querySelector('[data-variant-selector]');
      this.status = this.querySelector('[data-form-status]');
      this.addButton = this.querySelector('[data-add-button]');
      this.lightbox = this.querySelector('[data-gallery-lightbox]');
      this.lightboxImage = this.querySelector('[data-lightbox-image]');
      this.mobileGalleryQuery = window.matchMedia('(max-width: 620px)');
      this.activeSlide = 0;

      this.bindGallery();
      this.bindLightbox();
      this.bindOptions();
      this.bindForm();
      this.applyPackIntent();
      if (this.slides.length) this.showSlide(0, false);
      this.updatePrices();
    }

    bindGallery() {
      const controls = this.querySelector('[data-gallery-controls]');
      if (this.slides.length < 2) {
        if (controls) controls.hidden = true;
        return;
      }
      this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => this.showSlide(this.activeSlide - 1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.showSlide(this.activeSlide + 1));
      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.showSlide(Number(thumb.dataset.galleryThumb))));
      this.querySelector('[data-gallery-facts]')?.addEventListener('click', async () => {
        await this.showSlide(this.slides.length - 1);
        const stage = this.querySelector('[data-gallery-stage]');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        stage?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }

    bindLightbox() {
      const stage = this.querySelector('[data-gallery-stage]');
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      if (!stage || !trigger || !this.lightbox || !this.lightboxImage || typeof this.lightbox.showModal !== 'function') return;

      stage.addEventListener('click', (event) => {
        if (event.target.closest('button') || !event.target.closest('[data-gallery-slide]')) return;
        this.openLightbox();
      });
      trigger.addEventListener('click', () => this.openLightbox());
      this.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.closeLightbox());
      this.querySelector('[data-lightbox-previous]')?.addEventListener('click', () => this.showSlide(this.activeSlide - 1));
      this.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.showSlide(this.activeSlide + 1));
      this.lightbox.addEventListener('click', (event) => {
        if (!event.target.closest('button') && event.target !== this.lightboxImage) this.closeLightbox();
      });
      this.lightbox.addEventListener('close', () => this.finishLightboxClose());
      this.handleGalleryBreakpoint = (event) => {
        if (!event.matches) this.closeLightbox();
      };
      this.mobileGalleryQuery.addEventListener('change', this.handleGalleryBreakpoint);
    }

    openLightbox() {
      if (!this.mobileGalleryQuery.matches || !this.lightbox || this.lightbox.open) return;
      this.lightboxReturnFocus = this.querySelector('[data-gallery-lightbox-trigger]');
      this.updateLightboxImage(false);
      this.lockLightboxScroll();
      this.lightbox.showModal();
      this.querySelector('[data-lightbox-close]')?.focus({ preventScroll: true });
    }

    closeLightbox() {
      if (this.lightbox?.open) this.lightbox.close();
    }

    finishLightboxClose() {
      if (this.lightboxScrollLocked) {
        if (this.lightboxUsesSharedLock) {
          window.Wisp.unlockScroll();
        } else {
          document.body.style.overflow = this.previousBodyOverflow || '';
        }
        this.lightboxScrollLocked = false;
        this.lightboxUsesSharedLock = false;
      }
      this.lightboxReturnFocus?.focus({ preventScroll: true });
      this.lightboxReturnFocus = null;
    }

    lockLightboxScroll() {
      if (this.lightboxScrollLocked) return;
      this.previousBodyOverflow = document.body.style.overflow;
      this.lightboxUsesSharedLock = Boolean(window.Wisp && typeof window.Wisp.lockScroll === 'function');
      if (this.lightboxUsesSharedLock) {
        window.Wisp.lockScroll();
      } else {
        document.body.style.overflow = 'hidden';
      }
      this.lightboxScrollLocked = true;
    }

    updateLightboxImage(announce = true) {
      if (!this.lightboxImage) return;
      const sourceImage = this.slides[this.activeSlide]?.querySelector('img');
      if (!sourceImage) return;

      const request = (this.lightboxImageRequest || 0) + 1;
      const media = this.querySelector('[data-lightbox-media]');
      const source = sourceImage.dataset.lightboxSrc || sourceImage.currentSrc || sourceImage.getAttribute('src');
      this.lightboxImageRequest = request;
      media?.setAttribute('aria-busy', 'true');
      this.lightboxImage.alt = sourceImage.alt;
      this.lightboxImage.src = source;
      const count = this.querySelector('[data-lightbox-count]');
      const caption = this.querySelector('[data-lightbox-caption]');
      const status = this.querySelector('[data-lightbox-status]');
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      const position = this.activeSlide + 1;
      if (count) count.textContent = position + ' / ' + this.slides.length;
      if (caption) caption.textContent = sourceImage.alt;
      if (trigger) trigger.setAttribute('aria-label', 'Open image ' + position + ' of ' + this.slides.length + ' fullscreen');
      if (status && announce) status.textContent = 'Image ' + position + ' of ' + this.slides.length;

      const loaded = this.lightboxImage.decode ? this.lightboxImage.decode() : Promise.resolve();
      loaded.catch(() => {}).finally(() => {
        if (request === this.lightboxImageRequest) media?.removeAttribute('aria-busy');
      });
    }

    async showSlide(index, announce = true) {
      const nextSlide = (index + this.slides.length) % this.slides.length;
      const request = (this.slideRequest || 0) + 1;
      const incomingSlide = this.slides[nextSlide];
      const incomingImage = incomingSlide?.querySelector('img');
      const stage = this.querySelector('[data-gallery-stage]');
      this.slideRequest = request;

      if (incomingImage && (!incomingImage.complete || !incomingImage.naturalWidth)) {
        stage?.setAttribute('aria-busy', 'true');
        incomingImage.loading = 'eager';
        try {
          await incomingImage.decode();
        } catch (error) {}
        if (request !== this.slideRequest) return;
      }

      stage?.removeAttribute('aria-busy');
      if (nextSlide !== this.activeSlide) {
        const outgoingSlide = this.slides[this.activeSlide];
        outgoingSlide?.querySelectorAll('video, audio').forEach((media) => media.pause());
        outgoingSlide?.querySelectorAll('iframe[src]').forEach((iframe) => {
          try {
            const source = new URL(iframe.src, window.location.href);
            const provider = source.hostname.toLowerCase();
            if (provider === 'youtu.be' || provider.endsWith('.youtube.com') || provider.endsWith('.youtube-nocookie.com')) {
              iframe.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), source.origin);
            } else if (provider === 'vimeo.com' || provider.endsWith('.vimeo.com')) {
              iframe.contentWindow?.postMessage({ method: 'pause' }, source.origin);
            }
          } catch (error) {}
        });
      }
      this.activeSlide = nextSlide;
      this.slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== this.activeSlide; });
      this.thumbs.forEach((thumb, thumbIndex) => thumb.setAttribute('aria-pressed', String(thumbIndex === this.activeSlide)));
      const status = this.querySelector('[data-gallery-status]');
      if (status && announce) status.textContent = 'Media ' + (this.activeSlide + 1) + ' of ' + this.slides.length;
      const trigger = this.querySelector('[data-gallery-lightbox-trigger]');
      if (trigger) trigger.setAttribute('aria-label', 'Open image ' + (this.activeSlide + 1) + ' of ' + this.slides.length + ' fullscreen');
      if (this.lightbox?.open) this.updateLightboxImage();
    }

    bindOptions() {
      this.quantityOptions.forEach((option) => option.addEventListener('change', () => this.updatePrices()));
      this.purchaseOptions.forEach((option) => option.addEventListener('change', () => this.updatePrices()));
      this.variantSelector?.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.variantSelector.value);
        window.location.assign(url.toString());
      });
    }

    selectedQuantity() {
      const selected = this.quantityOptions.find((option) => option.checked);
      return selected ? Number(selected.value) : 1;
    }

    applyPackIntent() {
      const pack = Number(new URL(window.location.href).searchParams.get('pack'));
      if (![1, 2, 3].includes(pack)) return;
      const option = this.quantityOptions.find((candidate) => Number(candidate.value) === pack);
      if (option) option.checked = true;
    }

    selectedPurchase() {
      return this.purchaseOptions.find((option) => option.checked);
    }

    money(cents) {
      if (window.Wisp && typeof window.Wisp.money === 'function') return window.Wisp.money(Number(cents));
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD'
      }).format(Number(cents) / 100);
    }

    updatePrices() {
      const quantity = this.selectedQuantity();
      const plan = this.querySelector('[data-subscription-plan="' + quantity + '"]');
      const subscriptionOption = this.purchaseOptions.find((option) => option.hasAttribute('data-subscription-option'));
      if (subscriptionOption) {
        subscriptionOption.disabled = !plan;
        subscriptionOption.value = plan?.dataset.sellingPlanId || '';
        ['sellingPlanId', 'planCadence', 'planName'].forEach((key) => { subscriptionOption.dataset[key] = plan?.dataset[key] || ''; });
        if (!this.purchaseInitialized && plan) subscriptionOption.checked = true;
        if (!plan && subscriptionOption.checked) {
          subscriptionOption.checked = false;
          this.purchaseOptions.find((option) => option !== subscriptionOption).checked = true;
        }
        this.purchaseInitialized = true;
      }
      const quantityOption = this.quantityOptions.find((option) => option.checked);
      const purchase = this.selectedPurchase();
      const subscription = Boolean(purchase && purchase.dataset.sellingPlanId);
      const onceTotal = Number(quantityOption?.dataset.onceTotal || 0);
      const onceCompare = Number(quantityOption?.dataset.onceCompareValue || 0);

      const onceTotalSummary = this.querySelector('[data-once-total-summary]');
      if (onceTotalSummary) {
        onceTotalSummary.hidden = subscription;
        this.setMoney('[data-selected-once-total]', onceTotal);
      }
      const subscriptionTotals = this.querySelector('[data-subscription-totals]');
      if (subscriptionTotals) {
        subscriptionTotals.hidden = !subscription;
        if (subscription) {
          this.setMoney('[data-subscription-first-total]', Number(purchase.getAttribute('data-plan-total-' + quantity)));
          this.setMoney('[data-subscription-renewal-total]', Number(purchase.getAttribute('data-plan-renewal-total-' + quantity)));
        }
      }

      this.setMoney('[data-once-price]', Math.round(onceTotal / quantity));
      this.setCompare('[data-once-compare]', Math.round(onceCompare / quantity), Math.round(onceTotal / quantity));

      this.purchaseOptions.forEach((option) => {
        if (!option.dataset.sellingPlanId) return;
        const row = option.closest('.pdp-v2__purchase');
        const total = Number(option.getAttribute('data-plan-total-' + quantity));
        const compare = Number(option.dataset.unitCompare) * quantity;
        const extraSavings = onceTotal > total ? Math.round(((onceTotal - total) * 100) / onceTotal) : 0;
        this.setMoney('[data-plan-price]', Math.round(total / quantity), row);
        this.setCompare('[data-plan-compare]', Math.round(compare / quantity), Math.round(total / quantity), row);
        const savingsBadge = row.querySelector('[data-plan-save]');
        if (savingsBadge) {
          savingsBadge.hidden = extraSavings <= 0;
          savingsBadge.textContent = extraSavings + '% FIRST ORDER';
        }
      });

      this.quantityOptions.forEach((option) => {
        const optionQuantity = Number(option.value);
        const card = option.closest('.pdp-v2__quantity');
        const hasPlan = subscription && this.querySelector('[data-subscription-plan="' + optionQuantity + '"]');
        const total = hasPlan
          ? Number(purchase.getAttribute('data-plan-total-' + optionQuantity))
          : Number(option.dataset.onceTotal);
        const compare = hasPlan
          ? Number(purchase.dataset.unitCompare) * optionQuantity
          : Number(option.dataset.onceCompareValue);
        this.setMoney('[data-quantity-unit-price]', Math.round(total / optionQuantity), card);
        const compareUnit = card.querySelector('[data-quantity-compare-unit]');
        const compareUnitWrap = compareUnit?.closest('.pdp-v2__quantity-compare-unit');
        if (compareUnit && compareUnitWrap) {
          compareUnit.textContent = this.money(Math.round(compare / optionQuantity));
          compareUnitWrap.hidden = compare <= total;
        }
        this.setMoney('[data-quantity-total]', total, card);
        this.setCompare('[data-quantity-compare-total]', compare, total, card);

        const discount = hasPlan
          ? Number(purchase.getAttribute('data-plan-savings-' + optionQuantity) || 0)
          : Number(option.dataset.onceDiscount || 0);
        card.querySelectorAll('[data-quantity-discount]').forEach((badge) => {
          badge.hidden = discount <= 0;
          badge.textContent = hasPlan
            ? (optionQuantity === 3 ? 'SAVE ' : 'SAVE ') + Math.floor(discount) + '%'
            : optionQuantity === 3
              ? 'SAVE ' + Math.floor(discount) + '%'
              : 'SAVE ' + this.money(Number(option.dataset.onceSavings || 0));
        });
        const perk = card.querySelector('[data-quantity-perk]');
        if (perk && optionQuantity === 3) {
          perk.textContent = perk.dataset.onceLabel;
        }
      });
      this.dispatchEvent(new CustomEvent('pdp:purchase-change'));
    }

    setMoney(selector, cents, root = this) {
      const element = root.querySelector(selector);
      if (element) element.textContent = this.money(cents);
    }

    setCompare(selector, compare, total, root = this) {
      const element = root.querySelector(selector);
      if (!element) return;
      element.textContent = this.money(compare);
      element.hidden = compare <= total;
      const purchaseCompare = element.closest('.pdp-v2__purchase-compare');
      if (purchaseCompare) purchaseCompare.hidden = compare <= total;
    }

    bindForm() {
      if (!this.form || !this.addButton || !window.fetch || !window.FormData) return;
      this.form.addEventListener('submit', (event) => {
        if (!window.Wisp) return;
        this.addToCart(event);
      });
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.addButton.disabled || this.addButton.getAttribute('aria-busy') === 'true') return;

      const label = this.querySelector('[data-add-label]');
      const originalLabel = label?.textContent || '';
      let addConfirmed = false;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;
      if (label) label.textContent = this.dataset.addingLabel || 'ADDING...';
      this.setStatus('Adding item to cart.', 'status');

      try {
        await window.FuseCartQuantity.add(this.cartAddUrl(), new FormData(this.form));
        addConfirmed = true;

        this.setStatus('Item added to cart.', 'success');
        if (label) label.textContent = this.dataset.addedLabel || 'ADDED';
        try { window.Wisp.showToast?.('Added to cart'); } catch (toastError) {}
        try { window.Wisp.openCart?.(); } catch (cartOpenError) {}
      } catch (error) {
        if (addConfirmed || error.addConfirmed) {
          this.setStatus('Item added. Open your cart to finish updating subscription pricing.', 'status');
          if (label) label.textContent = this.dataset.addedLabel || 'ADDED';
          try { window.Wisp.openCart?.(); } catch (cartOpenError) {}
          return;
        }
        const message = error.transportError
          ? 'The cart request could not be confirmed. Check your cart before retrying.'
          : error.message || 'Could not add this item. Please try again.';
        this.setStatus(message, 'error');
        if (label) label.textContent = this.dataset.errorLabel || 'TRY AGAIN';
        try { window.Wisp.showToast?.(error.cartError ? error.message : message); } catch (toastError) {}
      } finally {
        window.setTimeout(() => {
          this.addButton.removeAttribute('aria-busy');
          this.addButton.disabled = false;
          if (label) label.textContent = originalLabel;
        }, 1200);
      }
    }

    cartAddUrl() {
      const url = this.dataset.cartAddUrl || '/cart/add';
      return url.endsWith('.js') ? url : url + '.js';
    }

    setStatus(message, state) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.dataset.state = state;
    }
  }

  customElements.define('pdp-product-v2', PdpProductV2);

  document.addEventListener('click', (event) => {
    const addTrigger = event.target.closest('[data-pdp-v2-cta-add]');
    const scrollTrigger = event.target.closest('[data-pdp-v2-cta-scroll]');
    if (!addTrigger && !scrollTrigger) return;
    const trigger = addTrigger || scrollTrigger;
    const productId = trigger.dataset.productId;
    const root = Array.from(document.querySelectorAll('pdp-product-v2')).find((candidate) => candidate.dataset.productId === productId);
    if (!root) return;
    event.preventDefault();
    if (addTrigger) root.querySelector('[data-add-button]')?.click();
    if (scrollTrigger) {
      root.querySelector('[data-pdp-v2-purchase]')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      root.querySelector('[data-quantity-option]:checked')?.focus({ preventScroll: true });
    }
  });
})();
